import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Elements } from '@stripe/react-stripe-js'
import { CheckBadgeIcon, CreditCardIcon, PlusIcon } from '@heroicons/react/24/outline'
// Card marks from react-payment-logos (MIT), not drawn here.
import Visa from 'react-payment-logos/dist/flat/Visa'
import Mastercard from 'react-payment-logos/dist/flat/Mastercard'
import Amex from 'react-payment-logos/dist/flat/Amex'
import Discover from 'react-payment-logos/dist/flat/Discover'
import Diners from 'react-payment-logos/dist/flat/Diners'
import Jcb from 'react-payment-logos/dist/flat/Jcb'
import Unionpay from 'react-payment-logos/dist/flat/Unionpay'
import Generic from 'react-payment-logos/dist/flat/Generic'
import { monetizationAPI } from '../../services/api'
import { getStripe, stripeKeyMode } from '../../services/stripe'
import type { PaymentMethod } from '../../types'
import ConfirmDialog from '../ConfirmDialog'
import Skeleton from '../Skeleton'
import { CardAddForm } from './PaymentMethodForm'
import { PaymentsNotSetUp, SecurePaymentsLine, TestModeNotice } from './StripeTrust'
import { ErrorPanel } from './SubscriptionsList'

/** Stripe's card.brand values; anything else gets the generic card mark. */
const BRANDS: Record<string, { name: string; Logo: typeof Visa }> = {
  visa: { name: 'Visa', Logo: Visa },
  mastercard: { name: 'Mastercard', Logo: Mastercard },
  amex: { name: 'American Express', Logo: Amex },
  discover: { name: 'Discover', Logo: Discover },
  diners: { name: 'Diners Club', Logo: Diners },
  jcb: { name: 'JCB', Logo: Jcb },
  unionpay: { name: 'UnionPay', Logo: Unionpay },
}

const brandOf = (method: PaymentMethod) =>
  BRANDS[(method.brand || '').toLowerCase()] ?? { name: 'Card', Logo: Generic }

const isExpired = (method: PaymentMethod, now = new Date()) =>
  method.exp_year != null &&
  method.exp_month != null &&
  (method.exp_year < now.getFullYear() ||
    (method.exp_year === now.getFullYear() && method.exp_month < now.getMonth() + 1))

const expiry = (method: PaymentMethod) =>
  method.exp_month != null && method.exp_year != null
    ? `${String(method.exp_month).padStart(2, '0')}/${String(method.exp_year).slice(-2)}`
    : null

const apiError = (error: any, fallback: string): string => error?.response?.data?.detail || fallback

interface PaymentMethodsPanelProps {
  /** Open the add-card form straight away (from a past-due subscription's "Update card"). */
  startAdding?: boolean
  onOpenPortal?: () => void
  isOpeningPortal?: boolean
}

const CardTileSkeleton: React.FC = () => (
  <li className="rounded-xl border border-gray-200 bg-white p-4" data-testid="card-skeleton">
    <div className="flex justify-between">
      <Skeleton className="h-8 w-12" />
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
    <Skeleton className="mt-5 h-5 w-32" />
    <Skeleton className="mt-3 h-3.5 w-24" />
  </li>
)

const CardTile: React.FC<{
  method: PaymentMethod
  busy: boolean
  onSetDefault: () => void
  onRemove: () => void
}> = ({ method, busy, onSetDefault, onRemove }) => {
  const { name, Logo } = brandOf(method)
  const expired = isExpired(method)
  const exp = expiry(method)
  return (
    <li
      className={`group relative flex flex-col rounded-xl border p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        method.is_default
          ? 'border-accent/60 bg-gradient-to-br from-blue-50 to-indigo-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      aria-label={`${name} ending in ${method.last4 ?? 'unknown'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <Logo width={48} height={30} role="img" aria-label={name} />
        {method.is_default && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
            <CheckBadgeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Default
          </span>
        )}
      </div>
      <p className="mt-5 font-mono text-lg tracking-widest text-gray-900">
        <span aria-hidden="true">••••&nbsp;</span>
        <span className="sr-only">ending in </span>
        {method.last4 ?? '????'}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {exp ? (
          <p className={`text-sm ${expired ? 'font-medium text-red-600' : 'text-gray-500'}`}>
            {expired ? `Expired ${exp}` : `Expires ${exp}`}
          </p>
        ) : (
          <span />
        )}
        <div className="flex gap-1">
          {!method.is_default && (
            <button
              type="button"
              onClick={onSetDefault}
              disabled={busy}
              className="rounded-md px-2 py-1 text-sm font-medium text-accent transition-colors hover:bg-accent/10 disabled:opacity-50"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="rounded-md px-2 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  )
}

const PaymentMethodsPanel: React.FC<PaymentMethodsPanelProps> = ({
  startAdding = false,
  onOpenPortal,
  isOpeningPortal = false,
}) => {
  const [methods, setMethods] = useState<PaymentMethod[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // The server has no Stripe key (503): the same "not set up" as no publishable key.
  const [stripeOff, setStripeOff] = useState(false)
  const [adding, setAdding] = useState(startAdding)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [removing, setRemoving] = useState<PaymentMethod | null>(null)
  const paymentsReady = stripeKeyMode() !== 'missing' && !stripeOff

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setMethods(await monetizationAPI.getPaymentMethods())
    } catch (err: any) {
      if (err?.response?.status === 503) setStripeOff(true)
      else setError(apiError(err, 'Couldn’t load your saved cards.'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  // The panel mounts when its tab opens, so saved cards load on opening the tab.
  useEffect(() => {
    load()
  }, [load])

  const handleAdd = async (method: { payment_method_id: string }) => {
    try {
      await monetizationAPI.addPaymentMethod(method)
    } catch (err: any) {
      throw new Error(apiError(err, 'Failed to add payment method'))
    }
    toast.success('Card saved')
    setAdding(false)
    await load()
  }

  const handleSetDefault = async (method: PaymentMethod) => {
    setBusyId(method.id)
    try {
      await monetizationAPI.setDefaultPaymentMethod(method.id)
      setMethods((all) => all.map((m) => ({ ...m, is_default: m.id === method.id })))
      toast.success('Default card updated')
    } catch (err: any) {
      toast.error(apiError(err, 'Couldn’t change your default card'))
    } finally {
      setBusyId(null)
    }
  }

  const confirmRemove = async () => {
    if (!removing) return
    const method = removing
    setBusyId(method.id)
    try {
      await monetizationAPI.removePaymentMethod(method.id)
      setRemoving(null)
      toast.success('Card removed')
      await load()
    } catch (err: any) {
      toast.error(apiError(err, 'Couldn’t remove that card'))
    } finally {
      setBusyId(null)
    }
  }

  const removingName = removing ? `${brandOf(removing).name} ending in ${removing.last4 ?? ''}` : ''

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Payment methods</h2>
          <p className="text-sm text-gray-500">Your default card pays for new subscriptions and renewals.</p>
        </div>
        {paymentsReady && !adding && !isLoading && !error && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg shadow-sm transition-colors hover:bg-accent-hover"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            Add payment method
          </button>
        )}
      </div>

      <TestModeNotice />

      {!paymentsReady ? (
        <PaymentsNotSetUp />
      ) : isLoading ? (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Loading saved cards">
          <CardTileSkeleton />
          <CardTileSkeleton />
        </ul>
      ) : error ? (
        <ErrorPanel message={error} onRetry={load} />
      ) : (
        <>
          {adding && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm motion-safe:animate-fade-in sm:p-5">
              <h3 className="mb-4 font-semibold text-gray-900">Add a card</h3>
              <Elements stripe={getStripe()}>
                <CardAddForm onSubmit={handleAdd} onCancel={() => setAdding(false)} showSecureLine={false} />
              </Elements>
            </div>
          )}

          {methods.length > 0 ? (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 motion-safe:animate-fade-in">
              {methods.map((method) => (
                <CardTile
                  key={method.id}
                  method={method}
                  busy={busyId === method.id}
                  onSetDefault={() => handleSetDefault(method)}
                  onRemove={() => setRemoving(method)}
                />
              ))}
            </ul>
          ) : (
            !adding && (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center motion-safe:animate-fade-in">
                <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
                  <CreditCardIcon className="h-6 w-6 text-accent" aria-hidden="true" />
                </span>
                <p className="font-medium text-gray-900">No saved cards</p>
                <p className="mt-1 text-sm text-gray-500">Add one to subscribe in a click.</p>
              </div>
            )
          )}
        </>
      )}

      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <SecurePaymentsLine />
        {onOpenPortal && paymentsReady && (
          <button
            type="button"
            onClick={onOpenPortal}
            disabled={isOpeningPortal}
            className="whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {isOpeningPortal ? 'Opening…' : 'Invoices on Stripe'}
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={removing != null}
        title="Remove this card?"
        message={`${removingName} will be removed from your account.`}
        confirmLabel="Remove card"
        cancelLabel="Keep it"
        danger
        confirming={removing != null && busyId === removing.id}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  )
}

export default PaymentMethodsPanel
